import React, { useEffect, useState, useCallback } from "react";
import { api, formatDateBR } from "../../lib/api";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const ROLES = [
  { value: "cedente_admin", label: "Admin Cedente", description: "Acesso total: equipe, clientes e configurações." },
  { value: "cedente_operator", label: "Operador", description: "Gerencia clientes e visualiza operações." },
  { value: "cedente_viewer", label: "Visualizador", description: "Apenas leitura." },
];

const ROLE_BADGE = {
  cedente_admin: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cedente_operator: "bg-violet-50 text-violet-800 border-violet-200",
  cedente_viewer: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

export default function CedenteUsers() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "cedente_admin";
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "cedente_operator", status: "active" });

  const load = useCallback(async () => {
    const { data } = await api.get("/cedente/users");
    setUsers(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", email: "", password: "", role: "cedente_operator", status: "active" });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, status: u.status });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, status: form.status };
        if (form.password) payload.password = form.password;
        await api.put(`/cedente/users/${editing.id}`, payload);
        toast.success("Usuário atualizado");
      } else {
        if (form.password.length < 6) {
          toast.error("Senha mínima de 6 caracteres");
          setSubmitting(false);
          return;
        }
        await api.post("/cedente/users", form);
        toast.success("Usuário criado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/cedente/users/${deleteTarget.id}`);
      toast.success("Usuário removido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="cedente-users-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="zemo-overline">Equipe</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">Usuários</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Gerencie quem da sua empresa pode acessar este portal.
          </p>
        </div>
        {isAdmin && (
          <Button
            className="bg-emerald-700 hover:bg-emerald-800 text-white h-10 font-semibold"
            onClick={openCreate}
            data-testid="ced-new-user-button"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Novo Usuário
          </Button>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {ROLES.map((r) => (
          <div key={r.value} className="zemo-card p-4">
            <Badge variant="outline" className={ROLE_BADGE[r.value]}>{r.label}</Badge>
            <div className="text-[12px] text-zinc-500 mt-2 leading-relaxed">{r.description}</div>
          </div>
        ))}
      </div>

      <div className="zemo-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/60 border-b border-[var(--zemo-border)] text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Nome</th>
                <th className="text-left px-5 py-3 font-semibold">E-mail</th>
                <th className="text-left px-5 py-3 font-semibold">Permissão</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Criado em</th>
                <th className="text-right px-5 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--zemo-border)]">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-zinc-50/60" data-testid={`ced-user-row-${u.email}`}>
                  <td className="px-5 py-3.5">
                    <div className="font-medium text-zinc-900">{u.name}</div>
                  </td>
                  <td className="px-5 py-3.5 text-zinc-700">{u.email}</td>
                  <td className="px-5 py-3.5">
                    <Badge variant="outline" className={ROLE_BADGE[u.role]}>
                      {ROLES.find((r) => r.value === u.role)?.label || u.role}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex items-center gap-1.5 text-[12px] font-semibold ${
                        u.status === "active" ? "text-emerald-700" : "text-zinc-500"
                      }`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${u.status === "active" ? "bg-emerald-500" : "bg-zinc-400"}`} />
                      {u.status === "active" ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-[12px] text-zinc-500">{formatDateBR(u.created_at)}</td>
                  <td className="px-5 py-3.5 text-right">
                    {isAdmin && (
                      <div className="inline-flex items-center gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`ced-edit-user-${u.email}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={u.id === me.id}
                          onClick={() => setDeleteTarget(u)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-30"
                          data-testid={`ced-delete-user-${u.email}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="ced-user-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar usuário" : "Novo usuário"}</DialogTitle>
            <DialogDescription>
              {editing ? "Atualize permissões e dados." : "Crie um usuário com nível de acesso adequado."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nome completo</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>E-mail</Label>
              <Input type="email" required disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{editing ? "Nova senha (opcional)" : "Senha"}</Label>
              <Input
                type="password"
                required={!editing}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? "Deixe em branco para manter" : "mínimo 6 caracteres"}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Permissão</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editing && (
              <div className="flex items-center justify-between rounded-md border border-[var(--zemo-border)] p-3">
                <div>
                  <div className="text-[13px] font-medium">Status ativo</div>
                  <div className="text-[11px] text-zinc-500">Inativos não conseguem entrar.</div>
                </div>
                <Switch
                  checked={form.status === "active"}
                  onCheckedChange={(v) => setForm({ ...form, status: v ? "active" : "inactive" })}
                />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="ced-user-form-submit">
                {submitting ? "Salvando…" : editing ? "Salvar" : "Criar usuário"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação removerá <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email}).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
